import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOldAssetsAttachmentState } from "store/selectors";
import { useTranslation } from "react-i18next";
import {
  Button,
  Header,
  Icon,
  Modal,
  Grid,
  Progress,
  Label,
  Accordion,
  Divider,
  Popup,
} from "semantic-ui-react";
import AssetsSelectorCarousel from "../AssetSelectorCarousel/AssetsSelectorCarousel";
import GlobalSearchInput from "../GlobalSearchInput/GlobalSearchInput";

function AssetSelectionModal(props) {
  const {
    data,
    open,
    setOpen,
    maxNumberOfAssets,
    assets,
    closeIcon,
    cancelButtonText,
    overallProgress,
    attachmentDownloads,
    attachingProcessError,
    handleSetClickedAssetId,
    attachNewAssets,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const errorText = () => {
    console.log("errorText", t("asset_loaded_error").split("."));
    const text = t("asset_loaded_error").split(".");
    return (
      <>
        {text.map((paragraph, p) => (
          <p key={p}>{paragraph}.</p>
        ))}
      </>
    );
  };

  let [isOnline, setIsOnline] = useState(navigator.onLine);

  const setOnline = () => {
    setIsOnline(true);
  };
  const setOffline = () => {
    setIsOnline(false);
  };

  useEffect(() => {
    window.addEventListener("offline", setOffline);
    window.addEventListener("online", setOnline);

    return () => {
      window.removeEventListener("offline", setOffline);
      window.removeEventListener("online", setOnline);
    };
  }, []);

  const { isLoading } = useSelector(getOldAssetsAttachmentState);

  const handleCloseModal = () => {
    setOpen(false);
    dispatch.attachmentProgress.resetAttachmentState();
    dispatch.deliveries.setOldAssetsSearchCriteria(undefined);
  };

  const selectedAssets = assets.filter((asset) => asset.isSelected);
  const [selectedItemsListOpen, setSelectedItemsListOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    overallProgress === "100" &&
      setTimeout(() => {
        dispatch.attachmentProgress.resetAttachmentState();
        setShowDetails(false);
      }, 2000);
  }, [overallProgress]);

  useEffect(() => {
    attachingProcessError &&
      setTimeout(() => {
        dispatch.attachmentProgress.loadStop();
        setShowDetails(false);
      }, 10000);
  }, [attachingProcessError]);

  return (
    <div>
      {open && (
        <Modal
          open={open}
          size="fullscreen"
          closeIcon={closeIcon}
          onClose={() => handleCloseModal()}
        >
          <Header icon>
            <Icon name={data.icon} />
            {data.headerText}
          </Header>
          <Modal.Content style={{ minHeight: "80vh" }}>
            <p>{data.text}</p>
            <GlobalSearchInput t={t} position={"left"} />
            {!isLoading && selectedAssets.length > 0 && (
              <div style={{ display: "flex", gap: "1rem", marginBottom: ".2rem" }}>
                <Accordion style={{ width: "250px" }} className="asset-selection">
                  <Accordion.Title
                    onClick={() => setSelectedItemsListOpen(!selectedItemsListOpen)}
                    style={{ marginBottom: "0 !important" }}
                  >
                    <strong>{t("selected_assets")}</strong>
                    <Label
                      style={{
                        marginLeft: "1rem",
                        backgroundColor: "#4700a3",
                        color: "white",
                      }}
                    >
                      {selectedAssets.length}
                    </Label>
                    <Icon
                      name="caret right"
                      style={{
                        transform: selectedItemsListOpen ? "rotate(90deg)" : "rotate(0)",
                        margin: "0 1rem 0 auto",
                      }}
                    />
                  </Accordion.Title>
                  <Accordion.Content
                    active={selectedItemsListOpen}
                    style={{
                      position: "absolute",
                      zIndex: "6",
                      background: "white",
                      marginTop: "1rem",
                    }}
                    className="selected-assets-list"
                  >
                    <ul
                      style={{
                        listStyleType: "none",
                        maxHeight: "170px",
                        overflow: "hidden",
                        overflowY: "auto",
                        padding: "0",
                      }}
                    >
                      {selectedAssets.map((asset, i) => (
                        <li
                          key={asset.id + i + 1}
                          aria-hidden="true"
                          onClick={() => handleSetClickedAssetId(asset.id)}
                          className="asset-name"
                        >
                          <Popup
                            content={<>Click to remove {asset.name} from selection list</>}
                            on="hover"
                            size="tiny"
                            trigger={
                              <div className="element-container">
                                <span className="asset-title">{asset.name}</span>
                                <span className="hidden-action">Remove from selection</span>
                              </div>
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  </Accordion.Content>
                </Accordion>
                {isOnline ? (
                  <Button
                    style={{
                      alignSelf: "center",
                      backgroundColor: "#4700a3",
                      color: "white",
                    }}
                    onClick={() => attachNewAssets(selectedAssets)}
                    disabled={!isOnline}
                  >
                    {t("add_files_to_campaign")}
                  </Button>
                ) : (
                  <Label
                    style={{
                      display: "flex",
                      alignSelf: "center",
                      padding: "1rem",
                      backgroundColor: "rgb(255, 189, 89)",
                    }}
                  >
                    <Icon name="warning sign" />
                    <span>Internet connection failed</span>
                  </Label>
                )}
              </div>
            )}
            {isLoading && (
              <Grid
                centered
                style={{ position: "absolute", width: "85%", top: "30%", left: "10%", zIndex: "2" }}
              >
                <Grid.Column>
                  <Grid.Row width={5} centered>
                    <Progress
                      className={
                        !attachingProcessError && overallProgress !== "100"
                          ? "bmo-purple-progress"
                          : undefined
                      }
                      percent={parseInt(overallProgress, 10)}
                      progress={!attachingProcessError}
                      success={overallProgress === "100"}
                      error={attachingProcessError}
                    >
                      {overallProgress === "100"
                        ? t("old_campaign_asset_attachment_success")
                        : attachingProcessError
                        ? errorText()
                        : t("old_campaign_asset_attachment_downloading")}
                    </Progress>
                    {attachmentDownloads.length > 1 && !attachingProcessError && (
                      <Accordion fluid styled>
                        <Accordion.Title
                          active={showDetails}
                          onClick={() => setShowDetails(!showDetails)}
                        >
                          <span>
                            <Icon name="dropdown" />
                          </span>
                          {t("delivery_upload_show_upload_details")}
                        </Accordion.Title>

                        <Accordion.Content active={showDetails}>
                          <div
                            style={{
                              height: "auto",
                              maxHeight: "45vh",
                              overflow: "scroll",
                              overflowX: "hidden",
                            }}
                          >
                            {attachmentDownloads.map((uploadProgress, i) => {
                              return (
                                <div key={uploadProgress.id}>
                                  {i > 0 && <Divider />}
                                  <Progress
                                    className={
                                      uploadProgress.totalDownloaded !== "100"
                                        ? "bmo-purple-progress"
                                        : undefined
                                    }
                                    percent={uploadProgress.totalDownloaded}
                                    progress
                                    success={uploadProgress.totalDownloaded === "100"}
                                  >
                                    <div>
                                      {t("delivery_upload_upload_progress_message")}{" "}
                                      <i>{uploadProgress.name}</i>
                                    </div>
                                  </Progress>
                                </div>
                              );
                            })}
                          </div>
                        </Accordion.Content>
                      </Accordion>
                    )}
                  </Grid.Row>
                </Grid.Column>
              </Grid>
            )}
            <AssetsSelectorCarousel
              maxNumberOfAssets={maxNumberOfAssets}
              assets={assets}
              open={open}
              renderFromModal
              handleSetClickedAssetId={(assetId) => handleSetClickedAssetId(assetId)}
              isNewAssetLoading={isLoading}
              attachingProcessError={attachingProcessError}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={() => handleCloseModal()}>
              <Icon name="remove" /> {cancelButtonText || t("modal_cancel")}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </div>
  );
}

export default AssetSelectionModal;
